import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { userActions } from "@_actions/user.actions";
import { catchError, getUserDataFromJwt } from "@_utils/helpers";
import subscriptionCopy from "@_configs/subscription.config.js";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import Button from "@mui/material/Button";
import UserRow from "@_components/UserRow/UserRow";
import UserContent from "@_components/UserContent/UserContent";
import UpgradePlanpopup from "@_components/popups/UpgradePlanPopup/UpgradePlanPopup.js";
import UpgradeImage from "@_assets/images/add_user.png";
import styles from "./UserManagement.module.css";
import Scrollbar from "react-perfect-scrollbar-z";
import "scrollbar.css";
import { sidebarActions } from "@_actions/sidebar.actions";
import { sidebarConstants } from "@_constants/sidebar.constants";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

function UserManagement() {
  const dispatch = useDispatch();
  const userReducer = useSelector((state) => state.userReducer);
  const jwtData = getUserDataFromJwt();

  const [displayUpgrade, setDisplayUpgrade] = useState(false);
  const {
    snackbarProps,
    showSnackbarSuccess,
    showSnackbarError,
    resetSnackbar,
  } = useSnackbar();
  const subscriptionReducer = useSelector((state) => state.subscriptionReducer);
  const { title, subtitle, copy_1, copy_2, upgradeButton } =
    subscriptionCopy.addUser[jwtData.subscription.toLowerCase()];

  const location = useLocation();
  const history = useHistory();
  const pathParts = location.pathname.split("/").filter(Boolean);

  useEffect(() => {
    dispatch(userActions.getUsers());
    dispatch(sidebarActions.updateStatus(sidebarConstants.USERS));
  }, [dispatch]);

  useEffect(() => {
    if (userReducer.status.USER_GET === "success") {
      if (pathParts.length === 4 && pathParts[2] === "users" && pathParts[3]) {
        const selectedUser = userReducer.users.list.find(
          (user) => user.user_id === pathParts[3],
        );
        if (selectedUser) {
          dispatch(userActions.selectUser(selectedUser));
          dispatch(userActions.addUserState(false));
        } else {
          history.push("/page_not_found");
        }
      }
      dispatch(userActions.clearStatusMessage("USER_GET"));
    } else if (catchError(userReducer.status.USER_GET)) {
      showSnackbarError(userReducer.status.USER_GET);
      dispatch(userActions.clearStatusMessage("USER_GET"));
    }
    // eslint-disable-next-line
  }, [dispatch, history, showSnackbarError, userReducer.status.USER_GET]);

  const handleCreateNewUser = () => {
    if (
      jwtData.subscription.toLowerCase() === "lite" &&
      userReducer.users.active >=
        subscriptionReducer.subscriptionConfig.max_users
    ) {
      setDisplayUpgrade(true);
    } else {
      if (pathParts.length === 4 && pathParts[2] === "users" && pathParts[3])
        history.push("/admin/account/users");
      dispatch(userActions.addUserState(true));
      dispatch(userActions.selectUser({}));
    }
  };

  // ERROR AND SUCCESS MESSAGES

  // display POST success & error
  useEffect(() => {
    if (userReducer.status.USER_POST === "success") {
      showSnackbarSuccess("Success!");
      dispatch(userActions.clearStatusMessage("USER_POST"));
    } else if (catchError(userReducer.status.USER_POST)) {
      showSnackbarError(userReducer.status.USER_POST, {
        autoHideDuration: 7000,
      });
      dispatch(userActions.clearStatusMessage("USER_POST"));
    } else return;
  }, [
    dispatch,
    resetSnackbar,
    showSnackbarError,
    showSnackbarSuccess,
    userReducer.status,
  ]);

  // display PUT success & error
  useEffect(() => {
    if (userReducer.status.USER_PUT === "success") {
      showSnackbarSuccess("Success!");
      if (userReducer.user_access_token) {
        const user = {
          email: userReducer.selected_user.email,
          accessToken: userReducer.user_access_token,
        };
        localStorage.setItem("user", JSON.stringify(user));
      }
      dispatch(userActions.getUsers());
      dispatch(userActions.clearStatusMessage("USER_PUT"));
    } else if (catchError(userReducer.status.USER_PUT)) {
      showSnackbarError(userReducer.status.USER_PUT, {
        autoHideDuration: 7000,
      });
      dispatch(userActions.clearStatusMessage("USER_PUT"));
    } else return;
    // eslint-disable-next-line
  }, [
    dispatch,
    resetSnackbar,
    showSnackbarError,
    showSnackbarSuccess,
    userReducer.status.USER_PUT,
  ]);

  // display activation success & error
  useEffect(() => {
    if (userReducer.status.USER_ACTIVATE_PUT === "success") {
      showSnackbarSuccess("Success!");
      dispatch(userActions.clearStatusMessage("USER_ACTIVATE_PUT"));
    } else if (catchError(userReducer.status.USER_ACTIVATE_PUT)) {
      showSnackbarError(userReducer.status.USER_ACTIVATE_PUT, {
        autoHideDuration: 4000,
      });
      dispatch(userActions.clearStatusMessage("USER_ACTIVATE_PUT"));
    } else return;
  }, [
    dispatch,
    resetSnackbar,
    showSnackbarError,
    showSnackbarSuccess,
    userReducer.status,
  ]);

  // display deactivation success & error
  useEffect(() => {
    if (userReducer.status.USER_DEACTIVATE_PUT === "success") {
      showSnackbarSuccess("Success!");
      dispatch(userActions.clearStatusMessage("USER_DEACTIVATE_PUT"));
    } else if (catchError(userReducer.status.USER_DEACTIVATE_PUT)) {
      showSnackbarError(userReducer.status.USER_DEACTIVATE_PUT, {
        autoHideDuration: 4000,
      });
      dispatch(userActions.clearStatusMessage("USER_DEACTIVATE_PUT"));
    } else return;
  }, [
    dispatch,
    resetSnackbar,
    showSnackbarError,
    showSnackbarSuccess,
    userReducer.status,
  ]);

  // display user DELETE success & error
  useEffect(() => {
    if (userReducer.status.USER_DELETE === "success") {
      history.push("/admin/account/users");
      showSnackbarSuccess("deletion successful!");
      dispatch(userActions.clearStatusMessage("USER_DELETE"));
    } else if (catchError(userReducer.status.USER_DELETE)) {
      showSnackbarError(userReducer.status.USER_DELETE, {
        autoHideDuration: 7000,
      });
      dispatch(userActions.clearStatusMessage("USER_DELETE"));
    } else return;
  }, [
    dispatch,
    history,
    resetSnackbar,
    showSnackbarError,
    showSnackbarSuccess,
    userReducer.status,
  ]);

  useEffect(() => {
    if (userReducer.status.USER_ACTIVATE_PUT === "success") {
      dispatch(userActions.getUsers());
    }
  }, [userReducer.status.USER_ACTIVATE_PUT, dispatch]);

  useEffect(() => {
    if (userReducer.status.USER_DEACTIVATE_PUT === "success") {
      dispatch(userActions.getUsers());
    }
  }, [userReducer.status.USER_DEACTIVATE_PUT, dispatch]);

  useEffect(() => {
    if (userReducer.status.USER_POST === "success") {
      dispatch(userActions.getUsers());
    }
  }, [userReducer.status.USER_POST, dispatch]);

  useEffect(() => {
    if (userReducer.status.USER_DELETE === "success") {
      dispatch(userActions.getUsers());
    }
  }, [userReducer.status.USER_DELETE, dispatch]);

  return (
    <div className={styles.root}>
      <AppSnackbar {...snackbarProps} />

      <div className={styles.user_management_container}>
        <div className={styles.user_list_container}>
          <Scrollbar
            options={{ suppressScrollX: true }}
            maxHeight="calc(100vh - 140px)"
          >
            <div className={styles.add_user_container}>
              <div className={styles.edit_icon_container}>
                <Button
                  className={styles.btn_add_user}
                  onClick={() => handleCreateNewUser()}
                >
                  <b className={styles.plus_sign}>+</b>Add New User
                </Button>
              </div>
            </div>
            <div className={styles.user_states_container}>
              <div className={styles.user_state_col}>
                <p>seats</p>
                <p>
                  <b>{userReducer.users.seats || 0}</b>
                </p>
                <p>
                  <b>booked</b>
                </p>
              </div>
              <div className={styles.user_state_col}>
                <p>users</p>
                <p>
                  <b>{userReducer.users.active || 0}</b>
                </p>
                <p>
                  <b>active</b>
                </p>
              </div>
              <div className={styles.user_state_col}>
                <p>
                  <b>{userReducer.users.invitation_pending || 0}</b>
                </p>
                <p>
                  <b>pending</b>
                </p>
              </div>
              <div className={styles.user_state_col}>
                <p>
                  <b>{userReducer.users.deactivated || 0}</b>
                </p>
                <p>
                  <b>deactivated</b>
                </p>
              </div>
            </div>

            {userReducer.users.list &&
              userReducer.users.list
                .filter(
                  (user) => user.state === "ACTIVE" && user.role === "ADMIN",
                )
                .map((filteredUser) => (
                  <UserRow user={filteredUser} key={filteredUser.user_id} />
                ))}

            {userReducer.users.list &&
              userReducer.users.list
                .filter(
                  (user) => user.state === "ACTIVE" && user.role === "AGENT",
                )
                .map((filteredUser) => (
                  <UserRow user={filteredUser} key={filteredUser.user_id} />
                ))}

            {userReducer.users.list &&
              userReducer.users.list
                .filter(
                  (user) =>
                    user.state === "INVITATION_PENDING" &&
                    user.role === "ADMIN",
                )
                .map((filteredUser) => (
                  <UserRow user={filteredUser} key={filteredUser.user_id} />
                ))}

            {userReducer.users.list &&
              userReducer.users.list
                .filter(
                  (user) =>
                    user.state === "INVITATION_PENDING" &&
                    user.role === "AGENT",
                )
                .map((filteredUser) => (
                  <UserRow user={filteredUser} key={filteredUser.user_id} />
                ))}

            {userReducer.users.list &&
              userReducer.users.list
                .filter(
                  (user) =>
                    user.state === "DEACTIVATED" && user.role === "ADMIN",
                )
                .map((filteredUser) => (
                  <UserRow user={filteredUser} key={filteredUser.user_id} />
                ))}

            {userReducer.users.list &&
              userReducer.users.list
                .filter(
                  (user) =>
                    user.state === "DEACTIVATED" && user.role === "AGENT",
                )
                .map((filteredUser) => (
                  <UserRow user={filteredUser} key={filteredUser.user_id} />
                ))}
          </Scrollbar>
        </div>
        <div className={styles.tabs_and_content_container}>
          {/* new user */}
          {userReducer.add_user_state ? <UserContent /> : null}

          {/* existing user */}
          {userReducer.selected_user &&
          Object.keys(userReducer.selected_user).length !== 0 ? (
            <UserContent />
          ) : null}

          {(userReducer.selected_user &&
            Object.keys(userReducer.selected_user).length === 0 &&
            !userReducer.add_user_state) ||
          (!userReducer.selected_user && !userReducer.add_user_state) ? (
            <div className={styles.user_no_data}>NO USER IS SELECTED!</div>
          ) : null}
        </div>
      </div>
      {displayUpgrade && (
        <UpgradePlanpopup
          title={title}
          subtitle={subtitle}
          open={displayUpgrade}
          imageUrl={UpgradeImage}
          upgradeButton={upgradeButton}
          copy_1={copy_1}
          copy_2={copy_2}
          close={() => setDisplayUpgrade(false)}
        />
      )}
    </div>
  );
}

export default UserManagement;
